// Branded single-statement PDF (clean letterhead style)
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { buildStatementText } from "./claimUtils";

const BRAND_NAME = "BeTheNexus.com";
const PAGE_W = 612;
const PAGE_H = 792;
const M = 64;
const C_TEXT = rgb(0.06, 0.09, 0.16);
const C_MUTED = rgb(0.39, 0.45, 0.55);
const C_BRAND = rgb(0.11, 0.30, 0.85);
const C_BRAND_DARK = rgb(0.06, 0.09, 0.16);
const C_BORDER = rgb(0.85, 0.88, 0.92);

function sanitize(t) {
  return String(t ?? "")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u00B7]/g, "*")
    .replace(/\u00A0/g, " ")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "");
}

function wrapLines(text, maxChars) {
  return String(text || "").split("\n").flatMap((para) => {
    const words = para.split(" ").filter(Boolean);
    const rows = [];
    let row = "";
    words.forEach((w) => {
      if ((row + " " + w).trim().length > maxChars) { rows.push(row); row = w; }
      else row = (row + " " + w).trim();
    });
    if (row) rows.push(row);
    if (rows.length === 0) rows.push("");
    return rows;
  });
}

export async function createStatementPdf({ statement, veteranName, statementType, overrideText = null }) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.TimesRoman);
  const bold = await doc.embedFont(StandardFonts.TimesRomanBold);
  const sans = await doc.embedFont(StandardFonts.Helvetica);
  const sansBold = await doc.embedFont(StandardFonts.HelveticaBold);

  const text = overrideText != null ? overrideText : buildStatementText(statement.type, statement.answers || {});
  const today = new Date().toLocaleDateString("en-US", { dateStyle: "long" });

  let page = doc.addPage([PAGE_W, PAGE_H]);
  const pages = [page];
  const draw = (str, x, y, opts = {}) => {
    page.drawText(sanitize(str), {
      x, y,
      size: opts.size || 11,
      font: opts.font || (opts.bold ? bold : font),
      color: opts.color || C_TEXT,
    });
  };

  // Letterhead
  page.drawRectangle({ x: 0, y: PAGE_H - 50, width: PAGE_W, height: 50, color: C_BRAND_DARK });
  page.drawRectangle({ x: 0, y: PAGE_H - 53, width: PAGE_W, height: 3, color: C_BRAND });
  page.drawRectangle({ x: M, y: PAGE_H - 38, width: 26, height: 18, color: C_BRAND });
  draw("BN", M + 6, PAGE_H - 32, { size: 10, bold: true, color: rgb(1, 1, 1), font: sansBold });
  draw(BRAND_NAME, M + 36, PAGE_H - 31, { size: 10, bold: true, color: rgb(1, 1, 1), font: sansBold });
  draw("Claim planning document - unofficial", PAGE_W - M - 175, PAGE_H - 31, { size: 8, color: rgb(0.75, 0.82, 0.95), font: sans });

  let y = PAGE_H - 90;
  draw((statementType?.title || "Statement").toUpperCase(), M, y, { size: 9, bold: true, color: C_BRAND, font: sansBold }); y -= 12;
  draw(statementType?.formNumber || "", M, y, { size: 9, color: C_MUTED, font: sans }); y -= 24;

  // Header block
  if (veteranName) { draw(`Re: ${veteranName}`, M, y, { size: 11, bold: true }); y -= 14; }
  if (statement.conditionName) { draw(`Condition: ${statement.conditionName}`, M, y, { size: 11 }); y -= 14; }
  draw(`Date: ${today}`, M, y, { size: 11 }); y -= 20;
  page.drawLine({ start: { x: M, y }, end: { x: PAGE_W - M, y }, thickness: 0.6, color: C_BORDER });
  y -= 20;

  if (!text) {
    draw("(This statement has no content yet. Add answers in the editor to populate the letter.)", M, y, { size: 10, color: C_MUTED });
    y -= 14;
  } else {
    // The statement is a series of paragraphs joined by blank lines. Render every paragraph as
    // body text — no section headers — so the document reads as a real letter rather than a form.
    const sections = text.split("\n\n");
    sections.forEach((section) => {
      const body = section.replace(/\n+/g, " ").trim();
      if (!body) return;
      wrapLines(body, 88).forEach((line) => {
        if (y < M + 80) { page = doc.addPage([PAGE_W, PAGE_H]); pages.push(page); y = PAGE_H - 80; }
        draw(line, M, y, { size: 11 });
        y -= 14;
      });
      y -= 8;
    });
  }

  // Signature block on last page (skip for nexus, which already has signature in answers)
  if (statement.type !== "nexus" && text) {
    if (y < M + 110) { page = doc.addPage([PAGE_W, PAGE_H]); pages.push(page); y = PAGE_H - 80; }
    y -= 24;
    draw("Signature: ", M, y, { size: 11, bold: true });
    page.drawLine({ start: { x: M + 70, y: y - 2 }, end: { x: M + 320, y: y - 2 }, thickness: 0.7, color: C_TEXT });
    y -= 24;
    draw("Printed name: ", M, y, { size: 11, bold: true });
    page.drawLine({ start: { x: M + 92, y: y - 2 }, end: { x: M + 320, y: y - 2 }, thickness: 0.7, color: C_TEXT });
    y -= 24;
    draw("Date: ", M, y, { size: 11, bold: true });
    page.drawLine({ start: { x: M + 42, y: y - 2 }, end: { x: M + 200, y: y - 2 }, thickness: 0.7, color: C_TEXT });
  }

  // Footer + page numbers
  pages.forEach((p, idx) => {
    p.drawLine({ start: { x: M, y: 50 }, end: { x: PAGE_W - M, y: 50 }, thickness: 0.5, color: C_BORDER });
    p.drawText(sanitize(`${BRAND_NAME} - Unofficial. Not an official VA form. See claim package disclaimer.`), {
      x: M, y: 36, size: 7, font: sans, color: C_MUTED,
    });
    const ps = `Page ${idx + 1} of ${pages.length}`;
    const w = sans.widthOfTextAtSize(ps, 7);
    p.drawText(ps, { x: PAGE_W - M - w, y: 36, size: 7, font: sansBold, color: C_MUTED });
  });

  return await doc.save();
}
